import { ReactNode, CSSProperties, useId } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Flex,
  Text,
} from '@backstage/ui';
import styles from './ModernRightDrawer.module.css';

export interface ModernRightDrawerProps {
  isOpen: boolean;
  title: string;
  children?: ReactNode;

  /** Custom header content (kicker, badges...); replaces the plain title. */
  header?: ReactNode;
  /** Custom footer content (ghost links, notes); ignored when a submit
   * button is configured. */
  footer?: ReactNode;

  submitButtonText?: string;
  onClose: () => void;
  onSubmit?: () => void;

  isLoading?: boolean;
  width?: number | string;
  hiddenBackdrop?: boolean;
}

export function ModernRightDrawer(props: Readonly<ModernRightDrawerProps>) {
  const {
    isOpen,
    title,
    header,
    footer,
    children,
    submitButtonText,
    onClose,
    onSubmit,
    isLoading,
    width = 420,
  } = props;

  const titleId = useId();

  const widthValue = typeof width === 'number' ? `${width}px` : width;

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={open => !open && onClose()}
      className={styles.drawer}
      style={{ '--modern-right-drawer-width': widthValue } as CSSProperties}
    >
      <DialogHeader>
        {header ?? (
          <Text as="h3" variant="title-small" weight="bold" id={titleId}>
            {title}
          </Text>
        )}
      </DialogHeader>

      <DialogBody>
        <Flex direction="column" gap="2">
          {children}
        </Flex>
      </DialogBody>

      {submitButtonText && onSubmit ? (
        <DialogFooter>
          <Button
            variant="primary"
            type="button"
            onPress={onSubmit}
            loading={isLoading}
          >
            {submitButtonText}
          </Button>
        </DialogFooter>
      ) : (
        footer && <DialogFooter>{footer}</DialogFooter>
      )}
    </Dialog>
  );
}
